import React, { useState, useRef, useEffect, startTransition, Suspense } from 'react';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Menu, 
  MenuItem, 
  Divider,
  ListItemIcon,
  ListItemText,
  Avatar,
  Badge,
  Fade,
  CircularProgress,
  useTheme,
  alpha,
  BottomNavigation,
  BottomNavigationAction,
  Paper
} from '@mui/material';
import { 
  Person as PersonIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
// React Icons imports
import { RiHome9Line, RiHome9Fill } from "react-icons/ri";
import { PiIdentificationBadgeLight, PiIdentificationBadgeFill } from "react-icons/pi";
import { FaConnectdevelop } from "react-icons/fa";
import { LiaConnectdevelop } from "react-icons/lia";
import { CiSettings } from "react-icons/ci";
import { IoSettings } from "react-icons/io5";
import { IoIosNotificationsOutline, IoIosNotifications } from "react-icons/io";
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import SuspenseWrapper from '../common/SuspenseWrapper';
import MobileErrorBoundary from '../common/MobileErrorBoundary';
import { PiPlugsConnected } from "react-icons/pi";
import { PiPlugsConnectedFill } from "react-icons/pi";
import { PiChatTeardropText, PiChatTeardropTextFill } from "react-icons/pi";
import { BsTicketDetailed, BsTicketDetailedFill } from "react-icons/bs";
import { useSafeArea } from '../../hooks/useSafeArea';
import { getSafeAreaPadding } from '../../utils/safeArea';



const MenuItemWithIcon = ({ icon, text, onClick }) => (
  <MenuItem onClick={onClick}>
    <ListItemIcon>
      {icon}
    </ListItemIcon>
    <ListItemText primary={text} />
  </MenuItem>
);

// Pull to refresh component
const PullToRefresh = ({ onRefresh, isRefreshing }) => {
  const theme = useTheme();
  const containerRef = useRef(null);
  const startY = useRef(0);
  const currentY = useRef(0);
  const refreshing = useRef(false);
  const [pullProgress, setPullProgress] = useState(0);

  const handleTouchStart = (e) => {
    if (containerRef.current && containerRef.current.scrollTop === 0) {
      startY.current = e.touches[0].clientY;
      refreshing.current = false;
    }
  };

  const handleTouchMove = (e) => {
    if (containerRef.current && containerRef.current.scrollTop === 0 && !refreshing.current) {
      currentY.current = e.touches[0].clientY;
      const pullDistance = Math.max(0, currentY.current - startY.current);
      const progress = Math.min(100, (pullDistance / 80) * 100);
      setPullProgress(progress);

      if (pullDistance > 80) {
        refreshing.current = true;
        onRefresh();
      }
    }
  };

  const handleTouchEnd = () => {
    setPullProgress(0);
  };

  return {
    containerRef,
    pullToRefreshHandlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    pullToRefreshProgress: (
      <Box sx={{ 
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: pullProgress > 0 ? 50 : 0,
        transition: 'height 0.2s',
        overflow: 'hidden',
        zIndex: 5
      }}>
        {isRefreshing ? (
          <CircularProgress size={24} color="primary" />
        ) : (
          pullProgress > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: theme.palette.primary.contrastText }}>
                {pullProgress >= 100 ? 'Release to refresh' : 'Pull to refresh'}
              </Typography>
            </Box>
          )
        )}
      </Box>
    )
  };
};

const MobileLayout = ({ children, title = 'Admin Dashboard' }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { unreadCount, isConnected, markAllAsRead } = useNotifications();
  const { user, logout } = useAuth();
  const { safeAreaInsets } = useSafeArea();
  const safeAreaTop = getSafeAreaPadding(safeAreaInsets, 'top');
  const safeAreaBottom = getSafeAreaPadding(safeAreaInsets, 'bottom');
  
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRouteTransitioning, setIsRouteTransitioning] = useState(false);
  
  // Set navigation value based on current path
  const [navValue, setNavValue] = useState(() => {
    const path = location.pathname;
    if (path === '/' || path === '/dashboard' || path === '/home') return 0;
    if (path.includes('/users')) return 1;
    if (path.includes('/plugs')) return 2;
    if (path.includes('/tickets')) return 3;
    if (path.includes('/settings')) return 4;
    return 0;
  });
  
  const { containerRef, pullToRefreshHandlers, pullToRefreshProgress } = 
    PullToRefresh({
      onRefresh: () => {
        // Prevent multiple refresh calls
        if (isRefreshing) return;
        
        setIsRefreshing(true);
        setTimeout(() => {
          setIsRefreshing(false);
          // Use React Router's navigate instead of window.location.reload()
          navigate(location.pathname, { replace: true });
        }, 1000);
      },
      isRefreshing
    });

  const profileOpen = Boolean(profileAnchorEl);
  const notificationOpen = Boolean(notificationAnchorEl);

  const handleProfileOpen = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };
  
  const handleProfileClose = () => {
    setProfileAnchorEl(null);
  };

  const handleNotificationOpen = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };
  
  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const navigateTo = (path) => {
    setIsRouteTransitioning(true);
    startTransition(() => {
      navigate(path);
      handleProfileClose();
    });
  };
  
  const handleNavChange = (event, newValue) => {
    setIsRouteTransitioning(true);
    startTransition(() => {
      setNavValue(newValue);
      
      switch(newValue) {
        case 0:
          navigate('/dashboard');
          break;
        case 1:
          navigate('/users');
          break;
        case 2:
          navigate('/plugs');
          break;
        case 3:
          navigate('/admin/tickets');
          break;
        case 4:
          navigate('/settings');
          break;
        default:
          navigate('/dashboard');
      }
    });
  };

  // Reset transition state when location changes
  useEffect(() => {
    setIsRouteTransitioning(false);
  }, [location.pathname]);

  // Update navigation value when location changes
  useEffect(() => {
    const path = location.pathname;
    if (path === '/' || path === '/dashboard' || path === '/home') {
      setNavValue(0);
    } else if (path.includes('/users')) {
      setNavValue(1);
    } else if (path.includes('/plugs')) {
      setNavValue(2);
    } else if (path.includes('/tickets')) {
      setNavValue(3);
    } else if (path.includes('/settings')) {
      setNavValue(4);
    }
  }, [location.pathname]);

  const handleMarkAllAsRead = async () => {
    startTransition(() => {
      try {
        markAllAsRead();
        handleNotificationClose();
      } catch (error) {
        console.error('Error marking notifications as read:', error);
      }
    });
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100vh', 
      position: 'relative',
      ...safeAreaTop
    }}>
      <AppBar position="sticky" elevation={0} sx={{
        background: theme.palette.background.default,
        backdropFilter: 'blur(5px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
        color: theme.palette.text.primary,
        pt: safeAreaInsets.top > 0 ? `${safeAreaInsets.top}px` : 0,
      }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'white' }}>
            {/* <DashboardIcon sx={{ mr: 1 }} /> */}
          </Box>
          <Typography variant="h4" sx={{ flexGrow: 1, fontWeight: 900, color: theme.palette.primary.main, 
          textTransform: 'uppercase', letterSpacing: '0.2em' , fontFamily: 'Kanit',}}>
            {/* {title} */}
            EASY HEALTH ADMIN
          </Typography>
          <IconButton 
            onClick={() => {
              startTransition(() => {
                navigate('/notifications');
              });
            }}
            sx={{ mr: 1 }}
          >
            <Badge 
              badgeContent={unreadCount || 0}
              color="error" 
              showZero={false}
              max={20}
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.55rem',
                  height: '25px',
                  minWidth: '25px',
                  padding: '0 3px',
                  borderRadius: '50%',
                  fontWeight: 'thin',
                  backgroundColor: '#f44336',
                  color: '#fff',
                  marginTop: '4px'
                }
              }}
            >
              <IoIosNotificationsOutline color={theme.palette.primary.main} size={30} mt={3} />
            </Badge>
          </IconButton>
          <IconButton color="inherit" onClick={handleProfileOpen}>
            <Avatar 
              src={user?.profile_photo} 
              sx={{ 
                bgcolor: theme.palette.primary.main,
                width: 32,
                height: 32,
                border: '2px solid rgba(255,255,255,0.2)'
              }}
            >
              {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Profile Menu */}
      <Menu
        anchorEl={profileAnchorEl}
        open={profileOpen}
        onClose={handleProfileClose}
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 220,
            borderRadius: 2,
            boxShadow: theme.shadows[8],
            
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Avatar 
              src={user?.profile_photo}
              sx={{ 
                width: 40, 
                height: 40,
                mr: 1.5,
                bgcolor: theme.palette.primary.main 
              }}
            >
              {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {user?.name || 'User'}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {user?.email}
              </Typography>
            </Box>
          </Box>
        </Box>
        
        <Divider />
        
        <MenuItemWithIcon 
          icon={<PersonIcon fontSize="small" />} 
          text="My Profile" 
          onClick={() => {
            startTransition(() => {
              navigateTo('/profile');
            });
          }}
        />
        
      

        <Divider />

        <MenuItem onClick={() => {
          startTransition(() => {
            handleProfileClose();
            logout();
            navigate('/login');
          });
        }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </MenuItem>
      </Menu>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationAnchorEl}
        open={notificationOpen}
        onClose={handleNotificationClose}
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 320,
            maxWidth: 400,
            maxHeight: 400,
            overflow: 'auto',
          }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Notifications</Typography>
          {unreadCount > 0 && (
            <Typography 
              variant="caption" 
              color="primary" 
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                startTransition(() => {
                  handleMarkAllAsRead();
                });
              }}
            >
              Mark all as read
            </Typography>
          )}
        </Box>
        
        <Divider />
        
        <Box sx={{ p: 2, textAlign: 'center' }}>
          {unreadCount > 0 ? (
            <Typography variant="body2" color="text.secondary">
              {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No new notifications
            </Typography>
          )}
        </Box>
      </Menu>

      {/* Content Container with Pull to Refresh */}
      <Box 
        ref={containerRef}
        {...pullToRefreshHandlers}
        sx={{ 
          flexGrow: 1,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          paddingBottom: '250px',
          paddingTop: '20px',
          background: theme.palette.background.default,
        }}
      >
        {pullToRefreshProgress}
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            style={{ padding: 4 }}
          >
            {isRouteTransitioning ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
              </Box>
                        ) : (
                          <MobileErrorBoundary>
                            <SuspenseWrapper>
                              {children}
                            </SuspenseWrapper>
                          </MobileErrorBoundary>
                        )}
          </motion.div>
        </AnimatePresence>
      </Box>
      
      {/* Bottom Navigation */}
      <Paper sx={{ 
        position: 'fixed', 
        bottom: 0, 
        left: 2, 
        right: 2, 
        zIndex: 'appBar',
        ...safeAreaBottom,
        pb: safeAreaInsets.bottom > 0 ? `${safeAreaInsets.bottom}px` : 0,
      }} elevation={3}>
        <BottomNavigation
          value={navValue}
          onChange={handleNavChange}
          showLabels
          sx={{
            height: {xs: '90px', md: '70px'},
            // background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 1)}, ${alpha(theme.palette.primary.light, 1)})`,
            backdropFilter: 'blur(5px)',
            background: theme.palette.background.default,
            WebkitBackdropFilter: 'blur(10px)',
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
            boxShadow: `0 -8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
            // pt: 2,
            pb: {xs: 2, md: 0},

           

            '& .MuiBottomNavigationAction-root': {
              color: theme.palette.primary.main,
              '&.Mui-selected': {
                color: theme.palette.primary.dark,
              }
            }
          }}
        >
          <BottomNavigationAction 
            label="Home" 
            icon={navValue === 0 ? <RiHome9Fill size={24} /> : <RiHome9Line size={24} />} 
          />
          <BottomNavigationAction 
            label="Users" 
            icon={navValue === 1 ? <PiIdentificationBadgeFill size={24} /> : <PiIdentificationBadgeLight size={24} />} 
          />
          <BottomNavigationAction 
            label="Plugs" 
            icon={navValue === 2 ? <PiPlugsConnectedFill size={24} /> : <PiPlugsConnected size={24} />} 
          />
          <BottomNavigationAction 
            label="Tickets"
            icon={navValue === 3 ? <BsTicketDetailedFill size={24} /> : <BsTicketDetailed size={24} />} 
          />
          <BottomNavigationAction 
            label="Settings" 
            icon={navValue === 4 ? <IoSettings size={24} /> : <CiSettings size={24} />} 
          />
        </BottomNavigation>
      </Paper>
    </Box>
  );
};

export default MobileLayout;